import React, { useState, useEffect } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import UserModal from '../../components/UserModal';
import userManagementService from '../../services/userManagementService';
import { toast } from 'react-toastify';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [filters, setFilters] = useState({
    role: '',
    entity_type: '',
    is_active: ''
  });

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userManagementService.getUsers(filters);
      if (response?.success) {
        setUsers(response.data);
      }
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
      toast.error('Kullanıcılar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadAgencies = async () => {
    try {
      const response = await userManagementService.getAgencies();
      if (response?.success) {
        setAgencies(response.data);
      }
    } catch (error) {
      console.error('Acenteler yüklenirken hata:', error);
    }
  };

  const loadCarriers = async () => {
    try {
      const response = await userManagementService.getCarriers();
      if (response?.success) {
        setCarriers(response.data);
      }
    } catch (error) {
      console.error('Taşıyıcılar yüklenirken hata:', error);
    }
  };

  const editUser = (user) => {
    setSelectedUser({ ...user });
    setShowEditModal(true);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedUser(null);
  };

  const handleSave = async (userData) => {
    try {
      if (selectedUser?.id) {
        // Güncelleme
        await userManagementService.updateUser(selectedUser.id, userData);
        toast.success('Kullanıcı başarıyla güncellendi');
      } else {
        // Yeni kullanıcı oluşturma
        if (userData.entity_type === 'agency') {
          await userManagementService.createAgencyUser(userData);
        } else if (userData.entity_type === 'carrier') {
          await userManagementService.createCarrierUser(userData);
        } else {
          await userManagementService.createSystemUser(userData);
        }
        toast.success('Kullanıcı başarıyla oluşturuldu');
      }
      closeModal();
      loadUsers();
    } catch (error) {
      console.error('Kullanıcı kaydedilirken hata:', error);
      toast.error('Kullanıcı kaydedilirken hata oluştu');
    }
  };

  const toggleUserStatus = async (user) => {
    try {
      await userManagementService.updateUser(user.id, { is_active: !user.is_active });
      toast.success(`Kullanıcı ${user.is_active ? 'pasifleştirildi' : 'aktifleştirildi'}`);
      loadUsers();
    } catch (error) {
      console.error('Kullanıcı durumu güncellenirken hata:', error);
      toast.error('Kullanıcı durumu güncellenirken hata oluştu');
    }
  };

  const getRoleText = (role) => {
    const roleTexts = {
      admin: 'Admin',
      manager: 'Manager',
      operation: 'Operasyon',
      finance: 'Finans',
      agent: 'Acente Temsilcisi',
      driver: 'Şoför',
      agency_user: 'Acente Kullanıcısı',
      carrier_user: 'Taşıyıcı Kullanıcısı'
    };
    return roleTexts[role] || role;
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      admin: 'bg-purple-100 text-purple-800',
      manager: 'bg-blue-100 text-blue-800',
      operation: 'bg-green-100 text-green-800',
      finance: 'bg-yellow-100 text-yellow-800',
      agent: 'bg-indigo-100 text-indigo-800',
      driver: 'bg-gray-100 text-gray-800',
      agency_user: 'bg-blue-100 text-blue-800',
      carrier_user: 'bg-green-100 text-green-800'
    };
    return classes[role] || 'bg-gray-100 text-gray-800';
  };

  useEffect(() => {
    loadUsers();
    loadAgencies();
    loadCarriers();
  }, []);

  return (
    <div className="p-6">
      {/* Başlık ve Yeni Kullanıcı Butonu */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Kullanıcı Yönetimi</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Yeni Kullanıcı
        </button>
      </div>

      {/* Filtreler */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
            <select 
              value={filters.role} 
              onChange={(e) => setFilters({...filters, role: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Tüm Roller</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="operation">Operasyon</option>
              <option value="finance">Finans</option>
              <option value="agent">Acente Temsilcisi</option>
              <option value="driver">Şoför</option>
              <option value="agency_user">Acente Kullanıcısı</option>
              <option value="carrier_user">Taşıyıcı Kullanıcısı</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Entity Türü</label>
            <select 
              value={filters.entity_type} 
              onChange={(e) => setFilters({...filters, entity_type: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Tümü</option>
              <option value="agency">Acente</option>
              <option value="carrier">Taşıyıcı</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
            <select 
              value={filters.is_active} 
              onChange={(e) => setFilters({...filters, is_active: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Tümü</option>
              <option value="true">Aktif</option>
              <option value="false">Pasif</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={loadUsers}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md"
            >
              Filtrele
            </button>
          </div>
        </div>
      </div>

      {/* Kullanıcı Listesi */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kullanıcı
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                      <div className="text-sm text-gray-500">@{user.username}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeClass(user.role)}`}>
                      {getRoleText(user.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.entity_type === 'agency' && user.agency ? (
                      <div>
                        <span className="text-blue-600">Acente:</span> {user.agency.name}
                      </div>
                    ) : user.entity_type === 'carrier' && user.carrier ? (
                      <div>
                        <span className="text-green-600">Taşıyıcı:</span> {user.carrier.name}
                      </div>
                    ) : (
                      <div className="text-gray-500">Sistem Kullanıcısı</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => editUser(user)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      Düzenle
                    </button>
                    <button
                      onClick={() => toggleUserStatus(user)}
                      className={user.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}
                    >
                      {user.is_active ? 'Pasifleştir' : 'Aktifleştir'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Kullanıcı Oluşturma/Düzenleme Modal */}
      {(showCreateModal || showEditModal) && (
        <UserModal
          show={showCreateModal || showEditModal}
          user={selectedUser}
          agencies={agencies}
          carriers={carriers}
          onClose={closeModal}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default UserManagement;
